
<script setup lang="ts">
import { computed, ref, watch, reactive } from 'vue';
import { useRoute } from 'vue-router';
import router from '../../router';
import { useClaimStore, useNotificationStore } from '../../store';
import { ScaleIcon, EnvelopeIcon, CommandLineIcon, ForwardIcon, MegaphoneIcon, PaperAirplaneIcon, ArrowTopRightOnSquareIcon } from '@heroicons/vue/24/solid';

import adminAPI from '../../services/api';
import { openBoxFolderForCurrentClaim } from "../../utils";
import downloadFileFromResponse from '../../services/api/download-file-from-response';
import DotLegalStep from '../ui/badges/DotLegalStep.vue';
import { storeToRefs } from 'pinia';
import { computedAsync } from '@vueuse/core';
import DisplayTicketHistory from "../reservation/DisplayTicketHistory.vue";
import { ConfidenceScoreOverview } from '@/interfaces';


const { claim } = storeToRefs(useClaimStore());
const route = useRoute();
function navigateToReservation() {
  const claimId = route.params.id;
  router.push(`/claims/${claimId}/passengers`);
}

async function confirmClaim() {
  const awaitNotification = useNotificationStore().emitNotification({
    type: "wait",
    title: "Validation et envoi de l'email en cours"
  });
  const { success } = await adminAPI.validation_askForAgentFeedback(claim.value.id, {
    agentEmail: agentEmail.value
  });
  if (success) {
    awaitNotification.duration = 0;
    useNotificationStore().emitNotification({
      title: "Demande de feedback envoyé à " + agentEmail.value
    });
    useClaimStore().retrieveClaim(claim.value.id);
  }
}

const agentEmail = ref("Chargement...");
watch(claim, async (newClaim) => {
  const claimEmails = await adminAPI.retrieveClaimContactEmails(newClaim.id);
  if (!claimEmails) return;
  const reservation = claim.value?.legalSpecification.impactedSegment.reservation
  if (!reservation) return;

  if (!reservation.travelAgencyOffice.isBusinessTravel && !reservation.travelAgencyCompany) {
  // Loisir : Envoi à l'agent
    agentEmail.value = claimEmails.reservationAgentEmail || claimEmails.fallbackOfficeEmail;
  } else {
  // Affaires : Envoi à l'email dédié à l'entreprise
    agentEmail.value = claimEmails.companyEmail || "(email entreprise vide)";
  }
}, { immediate: true });

const confidenceScore = ref<ConfidenceScoreOverview>();
watch(claim, async (newClaim) => {
  confidenceScore.value = await adminAPI.retrieveClaimConfidenceScore(newClaim.id);
}, { immediate: true });

const items = computed(() => ([
  {
    name: 'Valider',
    description: `Vérifier les doublons, société, circonstance extraordinaire, montant etc.`,
    iconColor: 'bg-purple-500',
    cta: {
      text: 'Envoyer',
      handleClick: confirmClaim,
    },
    icon: EnvelopeIcon,
  }
]));
</script>
<template>
  <div class="border-b bg-slate-50">
    <div class="max-w-lg py-5 mx-auto text-left">
    <div class="p-8 bg-white border rounded-md shadow">
      <h2 class="inline-flex text-base font-semibold leading-6 text-gray-900 align-middle">
        <!-- <div class="flex-none p-0.5 bg-red-400/30 rounded-full"><div class="h-1.5 w-1.5 rounded-full bg-red-500"></div></div> -->
        <div class="py-1 mr-2"><DotLegalStep :legal-step-name="useClaimStore().claim.claimLegalStep.name" /></div>
        Actions requises
      </h2>
      <DisplayTicketHistory :claim-id="claim.id" />
      <p class="mt-1 text-sm text-gray-500">Verifier le dossier avant de l'envoyer à l'agent.</p>
      <ul role="list" class="mt-6 border-t border-b border-gray-200 divide-y divide-gray-200">
        <li v-for="(item, itemIdx) in items" :key="itemIdx">
          <div class="relative flex items-start py-4 space-x-6 pointer-events-auto group">
            <div v-if="false" class="flex-shrink-0">
              <span :class="[item.iconColor, 'inline-flex h-10 w-10 items-center justify-center rounded-lg']">
                <component :is="item.icon" class="w-6 h-6 text-white" aria-hidden="true" />
              </span>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-900">
                <p>
                  <!-- <span class="absolute inset-0" aria-hidden="true" /> -->
                  {{ itemIdx + 1 }}. {{ item.name }}
                </p>
              </div>
              <p class="text-sm text-gray-500" v-html="item.description"/>
              <!-- Additional template -->
              <p class="mt-3 text-sm text-gray-500"> Email envoyé à : <span class="font-medium link">{{ agentEmail }}</span></p>
            </div>
            <div class="self-center flex-shrink-0">
              <button type="button"
                @click="() => item.cta.handleClick()"
                class="inline-flex cursor-pointer px-2 items-center gap-x-1.5 py-1.5 text-sm font-semibold text-gray-700 bg-white rounded shadow-sm group-hover:text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                <component :is="item.icon" class="w-4 h-4 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                {{ item.cta.text }}
              </button>
              
              <!-- <ChevronRightIcon class="w-5 h-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" /> -->
            </div>
          </div>
        </li>
      </ul>
      <div>
        <pre class="text-left">{{ confidenceScore }}</pre>
      </div>
      <div class="flex mt-6">
        <a @click.prevent="navigateToReservation" class="text-sm font-medium text-blue-600 cursor-pointer hover:text-blue-500">
          Voir le PNR
          <span aria-hidden="true"> &rarr;</span>
        </a>
      </div>
    </div>
  
    </div>
  </div>
  </template>
